/**
 * AttorneyWarRoomPage — Phase 09 Tasks 09.3 + 09.5 (frontend half).
 *
 * War-room view scoped to the attorney audience. Renders the union
 * returned by GET /api/v1/attorney/war-room/{report_id} plus a
 * challenge section (Phase 09d) that lets attorneys file challenges
 * against specific engine outputs.
 *
 * Auth: Clerk session (attorneys authenticate via Clerk only — the legacy
 * attorney JWT was retired). Missing/expired auth → redirect to
 * /attorney/login.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ConsensusView from '../components/consensus/ConsensusView';
import type { WarRoomConsensus } from '../types/warRoom';
import {
  ArrowLeft,
  AlertTriangle,
  Gavel,
  Loader2,
  MessageSquare,
  Send,
  ShieldCheck,
  Sparkles,
  X,
} from 'lucide-react';

import TimelineView from '../components/deliberation/TimelineView';
import IssueCard from '../components/deliberation/IssueCard';
import type {
  DeliberationEvent,
  DisputedIssue,
} from '../services/deliberationApi';
import type { components } from '../types/generated/api';
import { getAuthHeaders } from '../lib/authHeaders';
import { getCaseProfile } from '../services/caseProfileService';
import { UnconfirmedPostureBanner } from '../components/shared/UnconfirmedPostureBanner';
import type { CaseProfilePayload } from '../components/shared/caseProfile';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// Response shape — sourced from backend Pydantic via openapi-typescript
// codegen (Phase 09 Layer 1 retrofit). Renaming any field on the backend
// regenerates `api.ts` and breaks the `_PIN` constants below at compile
// time. The deliberation events/issues are still narrowed via the local
// DeliberationEvent / DisputedIssue helpers (those track the deliberation
// service contract, which is independent of the war-room HTTP shape).
// ---------------------------------------------------------------------------

type WarRoomReadResponse = components['schemas']['WarRoomReadResponse'];
type VerifierReport = components['schemas']['VerifierReport'];
type CheckFailed = components['schemas']['CheckFailed'];
type QueryResponse = components['schemas']['QueryResponse'];

// Compile-time field pins — rename any backend field → tsc fails here
// with the bad string in the error message.
const _WAR_ROOM_FIELD_PIN: ReadonlyArray<keyof WarRoomReadResponse> = [
  'report_id',
  'attorney_id',
  'deliberation',
  'consensus',
  'verifier',
  'query_prompt_context',
  'max_queries_remaining',
  'challenges_filed',
] as const;
void _WAR_ROOM_FIELD_PIN;

const _QUERY_RESPONSE_FIELD_PIN: ReadonlyArray<keyof QueryResponse> = [
  'answer',
  'referenced_events',
  'token_usage',
  'queries_today',
  'queries_remaining',
] as const;
void _QUERY_RESPONSE_FIELD_PIN;

// ---------------------------------------------------------------------------
// Subcomponents — small, scoped, no separate files (single-page PR)
// ---------------------------------------------------------------------------

function StatCard({
  label,
  value,
  hint,
  icon: Icon,
  accent,
}: {
  label: string;
  value: string | number;
  hint?: string;
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  accent: React.CSSProperties;
}) {
  return (
    <div
      className="border rounded-lg p-4 flex items-start gap-3"
      style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
      data-testid={`attorney-war-room-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" style={accent} />
      <div className="min-w-0">
        <p className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--color-text-label)' }}>{label}</p>
        <p className="text-2xl font-semibold mt-1 font-mono tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
          {value}
        </p>
        {hint && <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>{hint}</p>}
      </div>
    </div>
  );
}

function VerifierCard({ verifier }: { verifier: VerifierReport | null | undefined }) {
  if (!verifier) {
    return (
      <div
        className="border border-dashed rounded-lg p-4 text-sm"
        style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-muted)' }}
        data-testid="attorney-war-room-verifier-empty"
      >
        No verifier report on record yet.
      </div>
    );
  }
  const status = verifier.overall_status;
  const statusColor: React.CSSProperties =
    status === 'passed'
      ? { color: 'var(--color-verified)', backgroundColor: 'transparent', borderColor: 'var(--color-verified)' }
      : status === 'warnings'
      ? { color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }
      : { color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' };
  const failed = verifier.checks_failed || [];
  return (
    <div
      className="border rounded-lg p-4"
      style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
      data-testid="attorney-war-room-verifier"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Verifier Report</h3>
        <span className="text-xs font-medium px-2 py-0.5 rounded border" style={statusColor}>
          {status.toUpperCase()}
        </span>
      </div>
      {failed.length === 0 ? (
        <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>All checks passed.</p>
      ) : (
        <ul className="space-y-2">
          {failed.map((f: CheckFailed, i: number) => (
            <li
              key={`${f.check_name}-${i}`}
              className="text-xs border-l-2 pl-3"
              style={{ color: 'var(--color-text-secondary)', borderColor: 'var(--color-needs-human-tint-bd)' }}
            >
              <span className="font-medium">[{f.severity || '—'}]</span>{' '}
              {f.check_name}
              {f.details && <span style={{ color: 'var(--color-text-muted)' }}> — {f.details}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function QueryContextCard({ context }: { context: string }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      className="border rounded-lg p-4"
      style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}
      data-testid="attorney-war-room-query-context"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-1 text-xs font-medium"
        style={{ color: 'var(--color-needs-human-text)' }}
      >
        {expanded ? '▼' : '▶'} Query context (what the assistant sees as grounding)
      </button>
      {expanded && (
        <pre className="mt-3 text-xs whitespace-pre-wrap font-sans rounded p-3 border" style={{ color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
          {context}
        </pre>
      )}
    </div>
  );
}

// Local view-model — server returns QueryResponse; UI augments with the
// asked question for history rendering.
type QueryAnswer = QueryResponse & { question: string };

function QueryInterface({
  reportId,
  initialQueriesRemaining,
}: {
  reportId: string;
  initialQueriesRemaining: number;
}) {
  const [question, setQuestion] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState<QueryAnswer[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [escalation, setEscalation] = useState<string | null>(null);
  const [remaining, setRemaining] = useState<number>(initialQueriesRemaining);

  const canSubmit = question.trim().length >= 10 && !submitting && remaining > 0;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    setEscalation(null);
    const q = question.trim();
    try {
      const authHeaders = await getAuthHeaders();
      const res = await fetch(
        `${BACKEND_URL}/api/v1/attorney/war-room/${reportId}/query`,
        {
          method: 'POST',
          headers: {
            ...authHeaders,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ question: q }),
        },
      );
      if (res.status === 429) {
        const body = await res.json().catch(() => ({}));
        setEscalation(
          body.detail?.escalation_path || 'Daily query limit reached.',
        );
        setRemaining(0);
        return;
      }
      if (res.status === 401 || res.status === 403) {
        setError('Session expired. Please log in again.');
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        setError(body?.detail || `Failed (HTTP ${res.status})`);
        return;
      }
      const body: Omit<QueryAnswer, 'question'> = await res.json();
      setHistory((h) => [...h, { ...body, question: q }]);
      setRemaining(body.queries_remaining);
      setQuestion('');
    } catch (e: any) {
      setError(e?.message || 'Network error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      data-testid="attorney-query-interface"
      className="border rounded-lg p-5"
      style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4" style={{ color: 'var(--color-needs-human-text)' }} />
          <h2 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            Ask a question about this deliberation
          </h2>
        </div>
        <span
          data-testid="attorney-query-remaining"
          className="text-xs font-mono"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          {remaining}/20 remaining today
        </span>
      </div>
      <p className="text-xs mb-4" style={{ color: 'var(--color-text-muted)' }}>
        Grounded by the war-room deliberation. The assistant won't invent
        figures or cite sources outside the recorded reasoning.
      </p>

      {/* Conversation history */}
      {history.length > 0 && (
        <div className="space-y-3 mb-4" data-testid="attorney-query-history">
          {history.map((item, i) => (
            <div key={i} className="border-l-2 pl-3 py-1" style={{ borderColor: 'var(--color-needs-human-tint-bd)' }}>
              <p className="text-xs mb-1" style={{ color: 'var(--color-text-muted)' }}>Q: {item.question}</p>
              <p
                data-testid={`attorney-query-answer-${i}`}
                className="text-sm whitespace-pre-wrap"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                {item.answer}
              </p>
              {item.referenced_events.length > 0 && (
                <p className="text-xs mt-1" style={{ color: 'var(--color-text-dim)' }}>
                  References: {item.referenced_events.join(', ')}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="flex items-end gap-2">
        <textarea
          data-testid="attorney-query-input"
          rows={2}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="e.g. How was the revenue gap resolved?"
          disabled={remaining === 0 || submitting}
          className="flex-1 border rounded-md px-3 py-2 text-sm"
          style={{ borderColor: 'var(--color-border)' }}
        />
        <button
          data-testid="attorney-query-submit"
          disabled={!canSubmit}
          onClick={handleSubmit}
          className="px-4 py-2 text-sm font-medium rounded-md disabled:cursor-not-allowed flex items-center gap-2"
          style={canSubmit ? { color: 'var(--color-on-brand)', backgroundColor: 'var(--color-brand)' } : { color: 'var(--color-on-brand)', backgroundColor: 'var(--color-text-ghost)' }}
        >
          {submitting ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Send className="w-3.5 h-3.5" />
          )}
          Ask
        </button>
      </div>

      {/* Status messages */}
      {escalation && (
        <div
          data-testid="attorney-query-escalation"
          className="mt-3 border rounded-md p-3 text-xs"
          style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}
        >
          <strong>Daily limit reached.</strong> {escalation}
        </div>
      )}
      {error && !escalation && (
        <div className="mt-3 border rounded-md p-3 text-xs" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}>
          {error}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Challenge section — Phase 09d
// ---------------------------------------------------------------------------

const ENGINE_LIST: { id: string; display: string }[] = [
  { id: 'merchant_reconciliation', display: 'P&L Reconciliation' },
  { id: 'merchant_revenue_augmentation', display: 'Revenue Augmentation' },
  { id: 'bottom_up', display: 'Bottom-Up Loss' },
  { id: 'enhanced_claim', display: 'Enhanced Claim' },
  { id: 'dual_method', display: 'Dual-Method Reconciler' },
  { id: 'mitigation', display: 'Mitigation Deductions' },
  { id: 'impact', display: 'Impact Analysis' },
  { id: 'epi', display: 'Extended Period of Indemnity' },
  { id: 'policy_deductions', display: 'Policy Deductions' },
  { id: 'pv', display: 'Present Value' },
  { id: 'fair_settlement', display: 'Fair Settlement' },
  { id: 'case_strength', display: 'Case Strength' },
];

function ChallengeModal({
  reportId,
  engine,
  onClose,
  onSuccess,
}: {
  reportId: string;
  engine: { id: string; display: string };
  onClose: () => void;
  onSuccess: (res: any) => void;
}) {
  const [proposedChange, setProposedChange] = useState('');
  const [rationale, setRationale] = useState('');
  const [fieldId, setFieldId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [escalation, setEscalation] = useState<string | null>(null);

  const canSubmit = proposedChange.length >= 20 && rationale.length >= 50 && !submitting;

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    setEscalation(null);
    try {
      const authHeaders = await getAuthHeaders();
      const res = await fetch(
        `${BACKEND_URL}/api/v1/attorney/challenge/${reportId}`,
        {
          method: 'POST',
          headers: {
            ...authHeaders,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            target_engine_id: engine.id,
            target_field_id: fieldId || undefined,
            proposed_change: proposedChange,
            rationale,
          }),
        },
      );
      if (res.status === 429) {
        const body = await res.json();
        setEscalation(body.detail?.escalation_path || 'Maximum challenges reached.');
        return;
      }
      if (res.status === 401 || res.status === 403) {
        setError('Session expired. Please log in again.');
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        setError(body?.detail || `Failed (HTTP ${res.status})`);
        return;
      }
      const body = await res.json();
      onSuccess(body);
    } catch (e: any) {
      setError(e?.message || 'Network error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      data-testid="challenge-modal"
    >
      <div className="rounded-xl shadow-xl w-full max-w-lg border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--color-border-sub)' }}>
          <div className="flex items-center gap-2">
            <Gavel className="w-4 h-4" style={{ color: 'var(--color-needs-human-text)' }} />
            <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              Challenge: {engine.display}
            </h3>
          </div>
          <button onClick={onClose} style={{ color: 'var(--color-text-dim)' }}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Optional field ID */}
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
              Target field (optional)
            </label>
            <input
              type="text"
              data-testid="challenge-field-id"
              value={fieldId}
              onChange={(e) => setFieldId(e.target.value)}
              placeholder="e.g. revenue_q3"
              className="w-full border rounded-md px-3 py-2 text-sm"
              style={{ borderColor: 'var(--color-border)' }}
            />
          </div>

          {/* Proposed change */}
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
              Proposed change
              <span className="ml-2 font-normal" style={{ color: 'var(--color-text-dim)' }}>
                {proposedChange.length}/20 min
              </span>
            </label>
            <textarea
              data-testid="challenge-proposed-change"
              rows={3}
              value={proposedChange}
              onChange={(e) => setProposedChange(e.target.value)}
              placeholder="Describe what you think should change…"
              className="w-full border rounded-md px-3 py-2 text-sm"
              style={{ borderColor: 'var(--color-border)' }}
            />
          </div>

          {/* Rationale */}
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
              Rationale
              <span className="ml-2 font-normal" style={{ color: rationale.length >= 50 ? 'var(--color-verified)' : 'var(--color-text-dim)' }}>
                {rationale.length}/50 min
              </span>
            </label>
            <textarea
              data-testid="challenge-rationale"
              rows={4}
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              placeholder="Explain why this engine's output is incorrect (min 50 characters)…"
              className="w-full border rounded-md px-3 py-2 text-sm"
              style={{ borderColor: 'var(--color-border)' }}
            />
          </div>

          {/* Escalation (429) */}
          {escalation && (
            <div
              data-testid="challenge-escalation"
              className="border rounded-md p-3 text-xs"
              style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}
            >
              <strong>Challenge limit reached.</strong> {escalation}
            </div>
          )}

          {/* General error */}
          {error && !escalation && (
            <div className="border rounded-md p-3 text-xs" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-3 border-t rounded-b-xl" style={{ borderColor: 'var(--color-border-sub)', backgroundColor: 'var(--color-bg-elevated)' }}>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Cancel
          </button>
          <button
            data-testid="challenge-submit"
            disabled={!canSubmit}
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium rounded-md disabled:cursor-not-allowed flex items-center gap-2"
            style={canSubmit ? { color: 'var(--color-on-brand)', backgroundColor: 'var(--color-brand)' } : { color: 'var(--color-on-brand)', backgroundColor: 'var(--color-text-ghost)' }}
          >
            {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            File challenge
          </button>
        </div>
      </div>
    </div>
  );
}

function ChallengeSection({
  reportId,
  onChallengeSuccess,
}: {
  reportId: string;
  onChallengeSuccess: () => void;
}) {
  const [selectedEngine, setSelectedEngine] = useState<{ id: string; display: string } | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  function handleSuccess(res: any) {
    setSelectedEngine(null);
    setSuccessMsg(
      `Challenge filed (round ${res.round_number}/${res.round_number + res.remaining_rounds}). ` +
        `${res.remaining_rounds} round${res.remaining_rounds === 1 ? '' : 's'} remaining.`,
    );
    onChallengeSuccess();
    setTimeout(() => setSuccessMsg(null), 8000);
  }

  return (
    <div data-testid="attorney-challenge-section" className="border rounded-lg p-5" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      <div className="flex items-center gap-2 mb-3">
        <Gavel className="w-4 h-4" style={{ color: 'var(--color-needs-human-text)' }} />
        <h2 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>File a Challenge</h2>
      </div>
      <p className="text-xs mb-4" style={{ color: 'var(--color-text-muted)' }}>
        Challenge an engine's output to trigger re-deliberation. Maximum 2 challenges per engine.
      </p>

      {successMsg && (
        <div className="border rounded-md p-3 text-xs mb-4" style={{ backgroundColor: 'transparent', borderColor: 'var(--color-verified)', color: 'var(--color-verified)' }}>
          {successMsg}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {ENGINE_LIST.map((engine) => (
          <div
            key={engine.id}
            data-testid={`challenge-engine-${engine.id}`}
            className="flex items-center justify-between border rounded-md px-3 py-2"
            style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}
          >
            <span className="text-xs truncate mr-2" style={{ color: 'var(--color-text-secondary)' }}>{engine.display}</span>
            <button
              data-testid={`challenge-btn-${engine.id}`}
              onClick={() => {
                setSuccessMsg(null);
                setSelectedEngine(engine);
              }}
              className="flex-shrink-0 text-xs font-medium hover:underline"
              style={{ color: 'var(--color-needs-human-text)' }}
            >
              Challenge
            </button>
          </div>
        ))}
      </div>

      {selectedEngine && (
        <ChallengeModal
          reportId={reportId}
          engine={selectedEngine}
          onClose={() => setSelectedEngine(null)}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function AttorneyWarRoomPage() {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<WarRoomReadResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchKey, setFetchKey] = useState(0);
  const [caseProfile, setCaseProfile] = useState<CaseProfilePayload | null>(null);

  useEffect(() => {
    if (!reportId) return;
    let cancelled = false;
    getCaseProfile(reportId).then((cp) => {
      if (!cancelled) setCaseProfile(cp);
    });
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  useEffect(() => {
    if (!reportId) return;

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const authHeaders = await getAuthHeaders();
        if (!authHeaders.Authorization) {
          navigate('/attorney/login');
          return;
        }
        const res = await fetch(
          `${BACKEND_URL}/api/v1/attorney/war-room/${reportId}`,
          { headers: authHeaders },
        );
        if (cancelled) return;
        if (res.status === 401 || res.status === 403) {
          navigate('/attorney/login');
          return;
        }
        if (res.status === 404) {
          setError(
            'No War Room activity exists for this report yet. ' +
              'Trigger a deliberation run before opening this view.',
          );
          setData(null);
          return;
        }
        if (!res.ok) {
          setError(`Failed to load war room (HTTP ${res.status})`);
          return;
        }
        const body = await res.json();
        if (!cancelled) setData(body);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load war room');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reportId, navigate, fetchKey]);

  // Guard: schema marks deliberation non-null but server may omit it in degraded payloads.
  const deliberation = data?.deliberation as
    | components['schemas']['DeliberationView']
    | null
    | undefined;
  const issues = (deliberation?.issues ?? []) as unknown as DisputedIssue[];
  const events = (deliberation?.events ?? []) as unknown as DeliberationEvent[];

  if (!reportId) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-bg-base)' }}>
        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>No report ID provided.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-bg-base)' }} data-testid="attorney-war-room-page">
      {/* Header */}
      <div className="border-b px-6 py-4" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm mb-3"
            style={{ color: 'var(--color-text-muted)' }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <UnconfirmedPostureBanner profile={caseProfile} className="mb-4" />
          <h1 className="text-xl font-semibold font-serif" style={{ color: 'var(--color-text-primary)' }}>
            Attorney War Room
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
            Read-only view of the AI deliberation, consensus, and verifier
            output for report{' '}
            <code className="font-mono text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
              {reportId}
            </code>
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {loading && (
          <div
            className="border rounded-lg p-8 text-center text-sm"
            style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)', color: 'var(--color-text-muted)' }}
            data-testid="attorney-war-room-loading"
          >
            Loading war room…
          </div>
        )}

        {error && !loading && (
          <div
            className="border rounded-lg p-4 text-sm flex items-start gap-3"
            style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}
            data-testid="attorney-war-room-error"
          >
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>{error}</div>
          </div>
        )}

        {data && !loading && !error && (
          <>
            {/* Stat row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <StatCard
                label="Challenges filed"
                value={data.challenges_filed}
                hint="Across all engines for this report"
                icon={MessageSquare}
                accent={{ color: 'var(--color-needs-human-text)' }}
              />
              <StatCard
                label="Queries remaining"
                value={data.max_queries_remaining}
                hint="Per-report rate limit (placeholder until Phase 09e)"
                icon={ShieldCheck}
                accent={{ color: 'var(--color-needs-human-text)' }}
              />
            </div>

            {/* Challenge section — Phase 09d */}
            <ChallengeSection
              reportId={reportId}
              onChallengeSuccess={() => setFetchKey((k) => k + 1)}
            />

            {/* Query interface — Phase 09e */}
            <QueryInterface
              reportId={reportId}
              initialQueriesRemaining={data.max_queries_remaining}
            />

            {/* Two-column: consensus + verifier */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* data.consensus is the generated OpenAPI schema type (a structural
                  superset of ConsensusView's loose prop); runtime-compatible — the
                  component reads a subset. Bridge the nominal mismatch here. */}
              <ConsensusView consensus={data.consensus as unknown as WarRoomConsensus} defaultExpanded />
              <VerifierCard verifier={data.verifier} />
            </div>

            {/* Query-prompt context */}
            <QueryContextCard context={data.query_prompt_context} />

            {/* Deliberation: guard for degraded server payloads */}
            {deliberation == null ? (
              <div
                className="border border-dashed rounded-lg p-6 text-sm text-center"
                style={{
                  backgroundColor: 'var(--color-bg-elevated)',
                  borderColor: 'var(--color-border)',
                  color: 'var(--color-text-muted)',
                }}
                data-testid="attorney-war-room-deliberation-unavailable"
              >
                War Room deliberation unavailable
              </div>
            ) : (
              <>
                {/* Issues */}
                <div data-testid="attorney-war-room-issues">
                  <h2 className="text-sm font-semibold mb-3" style={{ color: 'var(--color-text-secondary)' }}>
                    Disputed issues ({issues.length})
                  </h2>
                  {issues.length === 0 ? (
                    <p className="text-xs italic" style={{ color: 'var(--color-text-muted)' }}>
                      No disputed issues recorded.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {issues.map((issue, idx) => (
                        <IssueCard
                          key={issue.tag || `issue-${idx}`}
                          issue={issue}
                          reportId={reportId}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Deliberation timeline */}
                <div data-testid="attorney-war-room-timeline">
                  <h2 className="text-sm font-semibold mb-3" style={{ color: 'var(--color-text-secondary)' }}>
                    Deliberation timeline ({events.length} events
                    {deliberation.events_truncated ? ', most recent shown' : ''})
                  </h2>
                  {events.length === 0 ? (
                    <p className="text-xs italic" style={{ color: 'var(--color-text-muted)' }}>
                      No deliberation events on record yet.
                    </p>
                  ) : (
                    <TimelineView events={events} />
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
